import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Car, Calendar, Gauge, Fuel, ArrowRight, Grid3x3, LayoutList, SlidersHorizontal, ShieldCheck } from 'lucide-react';
import { getAvailableCars } from '../services/firebaseService';
import { carSlug } from '../utils/carSlug';
import { setPageMeta } from '../utils/seo';

const Viaturas = () => {
  const [cars, setCars] = useState([]);
  const [filteredCars, setFilteredCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gridLayout, setGridLayout] = useState('3'); // '2', '3', 'list'
  const [sortBy, setSortBy] = useState('recent'); // 'recent', 'price-asc', 'price-desc', 'year-desc', 'km-asc'
  const [filters, setFilters] = useState({
    search: '',
    marca: '',
    precoMin: '',
    precoMax: '',
    anoMin: '',
    anoMax: '',
    kmMax: '',
    combustivel: ''
  });

  useEffect(() => {
    setPageMeta({
      title: 'Viaturas Usadas à Venda',
      description: 'Explore o nosso stock de viaturas usadas com garantia em Ovar. Carros certificados, preços transparentes e financiamento disponível na Diamond Cars.',
      path: '/viaturas'
    });

    const fetchCars = async () => {
      setLoading(true);
      const carsData = await getAvailableCars();
      setCars(carsData);
      setFilteredCars(carsData);
      setLoading(false);
    };
    fetchCars();
  }, []);

  useEffect(() => {
    let filtered = [...cars];

    // Apply filters
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(car =>
        car.marca?.toLowerCase().includes(searchLower) ||
        car.modelo?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.marca) {
      filtered = filtered.filter(car => car.marca === filters.marca);
    }

    if (filters.precoMin) {
      filtered = filtered.filter(car => car.preco >= parseFloat(filters.precoMin));
    }

    if (filters.precoMax) {
      filtered = filtered.filter(car => car.preco <= parseFloat(filters.precoMax));
    }

    if (filters.anoMin) {
      filtered = filtered.filter(car => car.ano >= parseInt(filters.anoMin));
    }

    if (filters.anoMax) {
      filtered = filtered.filter(car => car.ano <= parseInt(filters.anoMax));
    }

    if (filters.kmMax) {
      filtered = filtered.filter(car => car.km <= parseInt(filters.kmMax));
    }

    if (filters.combustivel) {
      filtered = filtered.filter(car => car.combustivel === filters.combustivel);
    }

    // Apply sorting
    switch (sortBy) {
      case 'price-asc':
        filtered.sort((a, b) => a.preco - b.preco);
        break;
      case 'price-desc':
        filtered.sort((a, b) => b.preco - a.preco);
        break;
      case 'year-desc':
        filtered.sort((a, b) => b.ano - a.ano);
        break;
      case 'km-asc':
        filtered.sort((a, b) => a.km - b.km);
        break;
      default: // recent
        filtered.sort((a, b) => {
          const dateA = a.createdAt?.seconds || 0;
          const dateB = b.createdAt?.seconds || 0;
          return dateB - dateA;
        });
    }

    setFilteredCars(filtered);
  }, [filters, cars, sortBy]);

  const marcasUnicas = [...new Set(cars.map(car => car.marca))].sort();
  const combustiveisUnicos = [...new Set(cars.map(car => car.combustivel).filter(Boolean))].sort();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price || 0);
  };

  const gridClass = {
    '2': 'grid-cols-1 md:grid-cols-2',
    '3': 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    'list': 'grid-cols-1'
  }[gridLayout];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Banner */}
      <section className="relative h-72 bg-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/ALX08396.jpeg"
            alt="Diamond Cars Stand"
            className="w-full h-full object-cover brightness-[0.4]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 via-transparent to-transparent"></div>
        </div>

        <div className="relative h-full container mx-auto px-4 flex items-center">
          <div className="text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-4">
              Nossas Viaturas
            </h1>
            <p className="text-xl text-blue-100">
              {filteredCars.length} {filteredCars.length === 1 ? 'viatura disponível' : 'viaturas disponíveis'}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="container mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Filtros */}
          <aside className="lg:w-80 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
              <div className="flex items-center gap-2 mb-6">
                <SlidersHorizontal className="w-5 h-5 text-blue-600" />
                <h2 className="text-xl font-bold text-gray-900">Filtros</h2>
              </div>

              <div className="space-y-6">
                {/* Pesquisa */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Pesquisar
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Marca ou modelo..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Marca */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Marca
                  </label>
                  <select
                    value={filters.marca}
                    onChange={(e) => setFilters({ ...filters, marca: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  >
                    <option value="">Todas</option>
                    {marcasUnicas.map(marca => (
                      <option key={marca} value={marca}>{marca}</option>
                    ))}
                  </select>
                </div>

                {/* Preço */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Preço
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="Mín (€)"
                      value={filters.precoMin}
                      onChange={(e) => setFilters({ ...filters, precoMin: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                    <input
                      type="number"
                      placeholder="Máx (€)"
                      value={filters.precoMax}
                      onChange={(e) => setFilters({ ...filters, precoMax: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Ano */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Ano
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="De"
                      value={filters.anoMin}
                      onChange={(e) => setFilters({ ...filters, anoMin: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                    <input
                      type="number"
                      placeholder="Até"
                      value={filters.anoMax}
                      onChange={(e) => setFilters({ ...filters, anoMax: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Quilómetros */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Quilómetros Máx
                  </label>
                  <input
                    type="number"
                    placeholder="ex: 100000"
                    value={filters.kmMax}
                    onChange={(e) => setFilters({ ...filters, kmMax: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>

                {/* Combustível */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Combustível
                  </label>
                  <select
                    value={filters.combustivel}
                    onChange={(e) => setFilters({ ...filters, combustivel: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  >
                    <option value="">Todos</option>
                    {combustiveisUnicos.map(comb => (
                      <option key={comb} value={comb}>{comb}</option>
                    ))}
                  </select>
                </div>

                {/* Limpar Filtros */}
                <button
                  onClick={() => setFilters({
                    search: '',
                    marca: '',
                    precoMin: '',
                    precoMax: '',
                    anoMin: '',
                    anoMax: '',
                    kmMax: '',
                    combustivel: ''
                  })}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 rounded-md font-semibold transition"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>
          </aside>

          {/* Grid de Carros */}
          <div className="flex-1">
            {/* Controles de Ordenação e Layout */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex items-center gap-4">
                <label className="text-sm font-semibold text-gray-700">
                  Ordenar por:
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                >
                  <option value="recent">Mais Recentes</option>
                  <option value="price-asc">Preço: Menor para Maior</option>
                  <option value="price-desc">Preço: Maior para Menor</option>
                  <option value="year-desc">Ano: Mais Recente</option>
                  <option value="km-asc">Quilómetros: Menor</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setGridLayout('2')}
                  className={`p-2 rounded-md transition ${
                    gridLayout === '2' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="2 colunas"
                >
                  <Grid3x3 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setGridLayout('3')}
                  className={`p-2 rounded-md transition ${
                    gridLayout === '3' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="3 colunas"
                >
                  <Grid3x3 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setGridLayout('list')}
                  className={`p-2 rounded-md transition ${
                    gridLayout === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="Lista"
                >
                  <LayoutList className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Loading */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="relative w-16 h-16">
                  <div className="absolute inset-0 border-4 border-blue-200 rounded-full"></div>
                  <div className="absolute inset-0 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
                <p className="mt-6 text-gray-600 font-medium text-lg">A carregar viaturas...</p>
              </div>
            ) : filteredCars.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-lg shadow-sm">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Nenhuma viatura encontrada
                </h3>
                <p className="text-gray-600">
                  Tente ajustar os seus critérios de pesquisa
                </p>
              </div>
            ) : (
              <div className={`grid ${gridClass} gap-6`}>
                {filteredCars.map(car => (
                  <Link
                    key={car._id}
                    to={`/carro/${carSlug(car)}`}
                    className="group bg-white rounded-lg shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden"
                  >
                    {/* Image */}
                    <div className={`relative bg-gray-200 overflow-hidden ${gridLayout === 'list' ? 'h-48' : 'h-64'}`}>
                      {car.images && car.images.length > 0 ? (
                        <img
                          src={car.images[0]}
                          alt={`${car.marca} ${car.modelo}`}
                          className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${car.vendido || car.reservado ? 'opacity-70' : ''}`}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                          <Car className="w-24 h-24 text-gray-300" />
                        </div>
                      )}
                      {/* Watermark VENDIDO / RESERVADO */}
                      {car.vendido && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-red-600 text-white px-6 py-3 rounded-lg shadow-xl transform -rotate-12">
                            <span className="text-2xl font-bold tracking-wider">VENDIDO</span>
                          </div>
                        </div>
                      )}
                      {car.reservado && !car.vendido && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-amber-500 text-white px-6 py-3 rounded-lg shadow-xl transform -rotate-12">
                            <span className="text-2xl font-bold tracking-wider">RESERVADO</span>
                          </div>
                        </div>
                      )}
                      <div className={`absolute top-4 right-4 ${car.vendido ? 'bg-gray-500' : car.reservado ? 'bg-amber-500' : 'bg-blue-600'} text-white px-4 py-2 rounded-md shadow-lg`}>
                        <span className="text-lg font-bold">
                          {formatPrice(car.preco)}
                        </span>
                      </div>
                      
                      {car.garantiaMeses > 0 && (
                        <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm text-blue-700 px-2 py-1 rounded shadow text-xs font-bold flex items-center gap-1">
                          <ShieldCheck className="w-3 h-3" />
                          {car.garantiaMeses} MESES
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition">
                        {car.marca} {car.modelo}
                      </h3>

                      {/* Specs */}
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="text-center">
                          <Calendar className="w-5 h-5 text-gray-500 mx-auto mb-1" />
                          <span className="text-sm font-semibold text-gray-700">{car.ano}</span>
                        </div>
                        <div className="text-center">
                          <Gauge className="w-5 h-5 text-gray-500 mx-auto mb-1" />
                          <span className="text-sm font-semibold text-gray-700">
                            {car.km?.toLocaleString('pt-PT')} km
                          </span>
                        </div>
                        {car.combustivel && (
                          <div className="text-center">
                            <Fuel className="w-5 h-5 text-gray-500 mx-auto mb-1" />
                            <span className="text-sm font-semibold text-gray-700">
                              {car.combustivel}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Button */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <span className="text-blue-600 font-semibold group-hover:text-blue-700">
                          Ver Detalhes
                        </span>
                        <ArrowRight className="w-5 h-5 text-blue-600 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Viaturas;
